/* @flow */
/* global google */
/* global navigator */

import React, { Component } from 'react';
import { Item, Form, Modal, Button, Grid, Checkbox } from 'semantic-ui-react';
import { error } from 'plugins/toastr';
import RadioMenu from 'shared/components/RadioMenu';
import {
  DEFAULT_MAP_BLOCKTYPE,
  ADDRESS_MAP_BLOCKTYPE,
  DESTINATION_MAP_BLOCKTYPE,
} from './constants';
import places from 'places.js';
import RichTextItem from 'shared/components/RichTextItem';
import RichTextCounter from 'shared/components/RichTextCounter';
import { EditorState } from 'draft-js';
import {
  htmlToState,
  linkDecorator,
  stateToHTML,
  getCharCount,
} from 'utils/richtext';
import { MAP_CHARACTER_LIMIT } from 'edito/constants/index';

type Props = {
  block: EditorialMapBlock,
  onSerialize?: (block: EditorialMapBlock) => void,
  onCanSubmitChanged?: (value: boolean) => void,
};

const mapBlockContentToEditorState = block =>
  (block.content
    ? htmlToState(block.content)
    : EditorState.createEmpty(linkDecorator));

class Edit extends Component {
  props: Props;

  map: HtmlElement;
  input: HtmlElement;

  constructor(props) {
    super(props);

    this.state = {
      editor: mapBlockContentToEditorState(props.block),
    };
  }

  onAddressChange = (evt: SyntheticEvent) => {
    if (evt.target instanceof HTMLInputElement) {
      const { block } = this.state;
      this.setState({ block: { ...block, address: evt.target.value } });
    }
  };

  onAddressClear = (evt: SyntheticEvent) => {
    const { marker } = this.state;
    const { block } = this.props;

    marker && marker.setMap(null);

    this.setState({
      block: { ...block, address: undefined, lat: undefined, lng: undefined },
    });
  };

  onTitleChange = (evt: SyntheticEvent) => {
    if (evt.target instanceof HTMLInputElement) {
      const { block } = this.props;
      this.props.onSerialize({ ...block, text: evt.target.value });
    }
  };

  onLatChange = (evt: SyntheticEvent) => {
    if (evt.target instanceof HTMLInputElement) {
      const { block } = this.state;
      this.props.onSerialize({ ...block, lat: evt.target.value });
    }
  };

  onLngChange = (evt: SyntheticEvent) => {
    if (evt.target instanceof HTMLInputElement) {
      const { block } = this.props;
      this.props.onSerialize({ ...block, lng: evt.target.value });
    }
  };

  toggleAddress = (evt: SyntheticEvent) => {
    evt.preventDefault();

    if (evt.target instanceof HTMLButtonElement) {
      const { block, onSerialize } = this.props;
      onSerialize({ ...block, type: ADDRESS_MAP_BLOCKTYPE });
    }
  };

  toggleDestination = (evt: SyntheticEvent) => {
    evt.preventDefault();

    if (evt.target instanceof HTMLButtonElement) {
      const { block, onSerialize } = this.props;
      onSerialize({ ...block, type: DESTINATION_MAP_BLOCKTYPE });
    }
  };

  onAddressSelect = (suggestion: Object) => {
    const { map, marker } = this.state;
    const { block } = this.props;

    map.setCenter(suggestion.latlng);

    let newMarker: Object;
    // Setting marker to new Location
    if (marker) {
      marker.setPosition(suggestion.latlng);
    } else {
      newMarker = new google.maps.Marker({
        position: suggestion.latlng,
        map,
      });
    }

    this.setState({
      marker: marker || newMarker,
      block: {
        ...block,
        address: suggestion.value,
        lat: suggestion.latlng.lat,
        lng: suggestion.latlng.lng,
      },
    });
  };

  onMapClick = (evt: MouseEvent) => {
    const { map, marker, block } = this.state;

    marker && marker.setMap(null);

    if (map) {
      const newMarker: Object = new google.maps.Marker({
        position: evt.latLng,
        animation: google.maps.Animation.DROP,
        map,
      });

      map.setCenter(evt.latLng);
      this.setState({
        marker: newMarker,
        block: {
          ...block,
          lat: evt.latLng.lat(),
          lng: evt.latLng.lng(),
        },
      });

      this.handleReverseGeocode();
    }
  };

  onMapShowChange = (event: SyntheticEvent, data: object) => {
    const { block, onSerialize } = this.props;

    onSerialize({ ...block, showMap: data.checked });
  };

  initMap = () => {
    const { block } = this.state;

    const mapCenterLat = block.lat || 46.248250;
    const mapCenterLng = block.lng || 0.878906;

    setTimeout(() => {
      const placeInput = places({
        container: this.addressInput,
      });

      placeInput.on('change', evt => {
        evt.suggestion && this.onAddressSelect(evt.suggestion);
      });

      placeInput.on('clear', evt => {
        this.onAddressClear();
      });

      if (this.map) {
        const map: Object = new google.maps.Map(this.map, {
          center: { lat: mapCenterLat, lng: mapCenterLng },
          zoom: block.zoomLevel || 11,
          zoomControl: true,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
        });

        let marker: Object;

        if (block.lat && block.lng) {
          marker = new google.maps.Marker({
            position: { lat: block.lat, lng: block.lng },
            map,
          });
        }

        map.addListener('click', this.onMapClick);
        map.addListener('zoom_changed', (evt: SyntheticEvent) => {
          this.setState({ block: { ...block, zoomLevel: map.getZoom() } });
        });

        this.setState({ map, marker });
      }
    }, 100);
    // TODO: find a way to initialize map without timeout (onMount and onOpen Modal callbacks doesn't seem to work)
  };

  serializeAsHtml = editorState => {
    const { block, onSerialize, onCanSubmitChanged } = this.props;

    this.setState({
      editor: editorState,
    });

    onSerialize({
      ...block,
      content: stateToHTML(editorState.getCurrentContent()),
    });

    onCanSubmitChanged(!(getCharCount(editorState) > MAP_CHARACTER_LIMIT));
  };

  componentDidMount() {
    const { onSerialize, block } = this.props;
    this.setState({ geocoder: new google.maps.Geocoder() });

    if (!block.type) {
      onSerialize({ ...block, type: DEFAULT_MAP_BLOCKTYPE });
    }
  }

  handleReverseGeocode = () => {
    const { geocoder, block } = this.state;

    if (geocoder && block.lat && block.lng) {
      geocoder.geocode(
        { location: { lat: block.lat, lng: block.lng } },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            this.setState({
              block: {
                ...block,
                address: results[0].formatted_address,
              },
            });
          }
        }
      );
    }
  };

  initWithMyPosition = (evt: SyntheticEvent) => {
    const { geocoder } = this.state;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        success => {
          const { block, onSerialize } = this.props;

          if (geocoder && success.coords.latitude && success.coords.longitude) {
            geocoder.geocode(
              {
                location: {
                  lat: success.coords.latitude,
                  lng: success.coords.longitude,
                },
              },
              (results, status) => {
                if (status === google.maps.GeocoderStatus.OK) {
                  onSerialize({
                    ...block,
                    address: results[0].formatted_address,
                    lat: success.coords.latitude,
                    lng: success.coords.longitude,
                  });
                }
              }
            );
          } else {
            error('Erreur de géolocalisation');
          }
        },
        failure => {
          switch (failure.code) {
            case 1:
              error("Access refusé par l'utilisateur");
              break;
            case 2:
              error('Service de géolocalisation indisponible');
              break;
            case 3:
              error('Requête expirée');
              break;
            default:
              error('Erreur inconnue');
              break;
          }
        }
      );
    } else {
      error('Geolocation is not supported by this browser');
    }
  };

  validateModal = (evt: SyntheticEvent) => {
    if (evt.target instanceof HTMLButtonElement) {
      const { block } = this.state;

      this.handleClose();
      this.props.onSerialize(block);
    }
  };

  handleClose = () => {
    this.setState({ openModal: false, block: undefined });
  };

  render() {
    const { openModal, editor } = this.state;
    const { block } = this.props;
    const menuDescriptor = [
      {
        value: ADDRESS_MAP_BLOCKTYPE,
        handler: this.toggleAddress,
        label: 'Adresse',
      },
      {
        value: DESTINATION_MAP_BLOCKTYPE,
        handler: this.toggleDestination,
        label: 'Destination',
      },
    ];
    const defaultLabel: String = block.type === ADDRESS_MAP_BLOCKTYPE
      ? 'Adresse'
      : 'Destination';
    const handleOpen = () => {
      this.setState({ openModal: true, block: this.props.block });
    };

    const renderModal = () => {
      const { block } = this.state;

      const addressValue = block ? block.address : '';
      const latValue = block && block.lat;
      const lngValue = block && block.lng;

      return (
        <Modal
          closeIcon={true}
          open={openModal}
          onClose={this.handleClose}
          onMount={this.initMap}
        >
          <Modal.Header>Mise à jour: {defaultLabel}</Modal.Header>
          <Modal.Content>
            <Modal.Description style={{ height: '550px' }}>
              <Form>
                <div className="field">
                  <label>Adresse</label>
                  <input
                    ref={ref => (this.addressInput = ref)}
                    type="text"
                    name="address"
                    value={addressValue || ''}
                    onChange={this.onAddressChange}
                  />
                </div>
                <Form.Group label="Coordonnées GPS" widths="equal" inline>
                  <Form.Input
                    label="Latitude"
                    value={latValue || ''}
                    name="lat"
                    onChange={this.onLatChange}
                    disabled
                  />
                  <Form.Input
                    label="Longitude"
                    value={lngValue || ''}
                    name="lng"
                    onChange={this.onLngChange}
                    disabled
                  />
                </Form.Group>
              </Form>
              <div
                ref={ref => (this.map = ref)}
                style={{ height: '75%', marginBottom: '5%' }}
              />
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button
              icon="check"
              content="Valider"
              primary
              onClick={this.validateModal}
            />
          </Modal.Actions>
        </Modal>
      );
    };

    return (
      <Item.Content>
        <Grid>
          <Grid.Row>
            <Grid.Column width="sixteen">
              <div className="ui form">
                <div className="field">
                  <label>Titre</label>
                  <input
                    type="text"
                    value={block.text || ''}
                    onChange={this.onTitleChange}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row verticalAlign="middle">
            <Grid.Column width="four">
              <RadioMenu
                label="Type de tuile"
                currentValue={block.type}
                menuDesc={menuDescriptor}
              />
            </Grid.Column>
            <Grid.Column width="four">
              <Checkbox
                checked={block.showMap || false}
                onChange={this.onMapShowChange}
                label="Afficher la carte"
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width="sixteen">
              <div className="ui form">
                <div className="field">
                  <label>{defaultLabel}</label>
                  <textarea
                    className="disabled"
                    value={block.address || ''}
                    rows="1"
                    readOnly
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width="five">
              <Button.Group>
                <Button type="button" attached="left" onClick={handleOpen}>
                  Mettre à jour
                </Button>
                <Button
                  type="button"
                  attached="right"
                  onClick={this.initWithMyPosition}
                >
                  Utiliser ma position
                </Button>
              </Button.Group>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width="sixteen">
              <div className="ui form">
                <div className="field">
                  <label>Informations supplémentaires</label>
                  <RichTextItem
                    parentState={editor}
                    onChange={this.serializeAsHtml}
                    withTextBlocks
                    withInlineBasics
                    withLinks
                    withColors
                  />
                  <RichTextCounter
                    editorState={editor}
                    charLimit={MAP_CHARACTER_LIMIT}
                    chars
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {renderModal()}
      </Item.Content>
    );
  }
}

export default Edit;



// WEBPACK FOOTER //
// ./src/modules/edito/plugins/address/Edit.js